import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck, 
    faTimes, 
    faFilter, 
    faDownload,
    faUserCheck,
    faUserTimes,
    faUsers,
    faEdit,
    faFileContract
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const InterviewerVerification = () => {
    const navigate = useNavigate();
    
    // Tab state
    const [activeTab, setActiveTab] = useState(0); // 0 = Interviewers, 1 = Controllers
    
    // Interviewers state
    const [interviewers, setInterviewers] = useState([]);
    const [interviewerStats, setInterviewerStats] = useState({ total: 0, verified: 0, unverified: 0, verified_percentage: 0 });
    
    // Controllers state
    const [controllers, setControllers] = useState([]);
    const [controllerStats, setControllerStats] = useState({ total: 0, verified: 0, unverified: 0, verified_percentage: 0 });
    
    // Common state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states
    const [filters, setFilters] = useState({
        status: '',
        verified: '',
        search: ''
    });
    
    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    
    // Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [pendingVerification, setPendingVerification] = useState(null);
    
    // Contract modal state
    const [showContractModal, setShowContractModal] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [contractForm, setContractForm] = useState({
        contrat_signed: false,
        contract_start_date: '',
        contract_end_date: ''
    });
    
    // Scroll to top button state
    const [showScrollTop, setShowScrollTop] = useState(false);
    
    // Tab content refs for height animation
    const tabContentRef = useRef(null);
    const [tabContentHeight, setTabContentHeight] = useState('auto');

    useEffect(() => {
        if (activeTab === 0) {
            fetchInterviewers();
            fetchInterviewerStats();
        } else {
            fetchControllers();
            fetchControllerStats();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 0) {
            fetchInterviewers();
        } else {
            fetchControllers();
        }
    }, [filters]);

    // Handle scroll to show/hide scroll top button
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const fetchInterviewers = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value !== '')
            );

            const response = await api.get('/interviewers', { params });
            
            if (response.data.success) {
                setInterviewers(response.data.data);
            } else {
                setError('Erreur lors du chargement des enquêteurs');
            }
        } catch (error) {
            setError('Erreur lors du chargement des enquêteurs');
            console.error('Error fetching interviewers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchControllers = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value !== '')
            );

            const response = await api.get('/controllers', { params });
            
            if (response.data.success) {
                const data = Array.isArray(response.data.data) ? response.data.data : response.data.data.data || [];
                setControllers(data);
            } else {
                setError('Erreur lors du chargement des contrôleurs');
            }
        } catch (error) {
            setError('Erreur lors du chargement des contrôleurs');
            console.error('Error fetching controllers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchInterviewerStats = async () => {
        try {
            const response = await api.get('/interviewers/verification/stats');
            if (response.data.success) {
                setInterviewerStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching interviewer stats:', error);
        }
    };

    const fetchControllerStats = async () => {
        try {
            const response = await api.get('/controllers/verification/stats');
            if (response.data.success) {
                setControllerStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching controller stats:', error);
        }
    };

    const handleTabClick = (index) => {
        setActiveTab(index);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSearchChange = (value) => {
        setSearchInput(value);
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleVerificationClick = (personId, currentVerified, personName, personType) => {
        setPendingVerification({
            id: personId,
            currentVerified: currentVerified,
            newVerified: currentVerified === 'yes' ? 'no' : 'yes',
            name: personName,
            type: personType
        });
        setShowConfirmModal(true);
    };

    const handleConfirmVerification = async () => {
        if (!pendingVerification) return;
        
        try {
            const endpoint = pendingVerification.type === 'interviewer' 
                ? `/interviewers/${pendingVerification.id}/verification`
                : `/controllers/${pendingVerification.id}/verification`;
            
            const response = await api.put(endpoint, {
                verified: pendingVerification.newVerified
            });

            if (response.data.success) {
                if (pendingVerification.type === 'interviewer') {
                    setInterviewers(prev => 
                        prev.map(person => 
                            person.id === pendingVerification.id 
                                ? { ...person, verified: pendingVerification.newVerified }
                                : person
                        )
                    );
                    await fetchInterviewerStats();
                } else {
                    setControllers(prev => 
                        prev.map(person => 
                            person.id === pendingVerification.id 
                                ? { ...person, verified: pendingVerification.newVerified }
                                : person
                        )
                    );
                    await fetchControllerStats();
                }
                
                const action = pendingVerification.newVerified === 'yes' ? 'vérifié' : 'non vérifié';
                const personLabel = pendingVerification.type === 'interviewer' ? 'enquêteur' : 'contrôleur';
                setSuccessMessage(`Le ${personLabel} "${pendingVerification.name}" a été marqué comme ${action}.`);
                setShowSuccessModal(true);
            }
        } catch (error) {
            console.error('Error updating verification:', error);
            setSuccessMessage('Erreur lors de la mise à jour du statut de vérification.');
            setShowSuccessModal(true);
        } finally {
            setShowConfirmModal(false);
            setPendingVerification(null);
        }
    };

    const handleContractClick = (person, personType) => {
        setSelectedPerson({ ...person, type: personType });
        setContractForm({
            contrat_signed: person.contrat_signed || false,
            contract_start_date: person.contract_start_date || '',
            contract_end_date: person.contract_end_date || ''
        });
        setShowContractModal(true);
    };

    const handleContractSubmit = async () => {
        if (!selectedPerson) return;
        
        try {
            const endpoint = selectedPerson.type === 'interviewer'
                ? `/interviewers/${selectedPerson.id}/verification`
                : `/controllers/${selectedPerson.id}/verification`;
            
            const response = await api.put(endpoint, {
                verified: selectedPerson.verified,
                ...contractForm
            });

            if (response.data.success) {
                if (selectedPerson.type === 'interviewer') {
                    setInterviewers(prev => 
                        prev.map(person => 
                            person.id === selectedPerson.id 
                                ? response.data.data
                                : person
                        )
                    );
                } else {
                    setControllers(prev => 
                        prev.map(person => 
                            person.id === selectedPerson.id 
                                ? response.data.data
                                : person
                        )
                    );
                }
                
                setSuccessMessage('Informations du contrat mises à jour avec succès.');
                setShowSuccessModal(true);
                setShowContractModal(false);
                setSelectedPerson(null);
            }
        } catch (error) {
            console.error('Error updating contract:', error);
            setSuccessMessage('Erreur lors de la mise à jour du contrat.');
            setShowSuccessModal(true);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <FontAwesomeIcon icon={faCheck} className="text-green-600" />;
            case 'suspended':
                return <FontAwesomeIcon icon={faTimes} className="text-red-600" />;
            case 'inactive':
                return <FontAwesomeIcon icon={faTimes} className="text-gray-400" />;
            default:
                return <FontAwesomeIcon icon={faTimes} className="text-gray-400" />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active':
                return 'Actif';
            case 'suspended':
                return 'Suspendu';
            case 'inactive':
                return 'Inactif';
            default:
                return 'Inconnu';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'text-green-600';
            case 'suspended':
                return 'text-red-600';
            case 'inactive':
                return 'text-gray-400';
            default:
                return 'text-gray-400';
        }
    };

    const handleRowClick = (person, personType, event) => {
        // Don't navigate if clicking on buttons, inputs, or links
        if (event.target.tagName === 'BUTTON' || 
            event.target.tagName === 'INPUT' || 
            event.target.tagName === 'LABEL' ||
            event.target.closest('button') ||
            event.target.closest('input') ||
            event.target.closest('label')) {
            return;
        }

        if (personType === 'controller') {
            navigate(`/controllers/${person.id}`);
        } else if (personType === 'interviewer') {
            // Check if interviewer is CATI
            if (person.is_cati) {
                navigate(`/cati/interviewers/${person.id}`);
            } else {
                navigate(`/interviewers/${person.id}`);
            }
        }
    };

    // Get current data based on active tab
    const currentData = activeTab === 0 ? interviewers : controllers;
    const currentStats = activeTab === 0 ? interviewerStats : controllerStats;
    const dataType = activeTab === 0 ? 'interviewer' : 'controller';
    const dataLabel = activeTab === 0 ? 'Enquêteurs' : 'Contrôleurs';
    const dataLabelSingular = activeTab === 0 ? 'enquêteur' : 'contrôleur';

    const filteredData = currentData.filter(person => {
        if (filters.search && !person.name.toLowerCase().includes(filters.search.toLowerCase()) && 
            !(person.ref || '').toLowerCase().includes(filters.search.toLowerCase()) &&
            !person.email.toLowerCase().includes(filters.search.toLowerCase())) return false;
        return true;
    });

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Vérification et Contrats
                </h1>
                <p className="text-gray-600">
                    Gérez la vérification et les contrats des enquêteurs et contrôleurs
                </p>
            </div>

            {/* Adaptive Tabs */}
            <div className="wrapper mb-6">
                <ul className="tabs">
                    <li 
                        className={activeTab === 0 ? 'active' : ''}
                        onClick={() => handleTabClick(0)}
                    >
                        Enquêteurs
                    </li>
                    <li 
                        className={activeTab === 1 ? 'active' : ''}
                        onClick={() => handleTabClick(1)}
                    >
                        Contrôleurs
                    </li>
                </ul>
                <ul className="tab__content" style={{ height: tabContentHeight }}>
                    {/* Interviewers Tab Content */}
                    <li className={activeTab === 0 ? 'active' : ''} style={{ display: activeTab === 0 ? 'block' : 'none' }}>
                        <div className="content__wrapper">
                            {/* Statistics */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-full bg-blue-100">
                                            <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Total Enquêteurs</p>
                                            <p className="text-2xl font-bold text-gray-900">{interviewerStats.total}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-full bg-green-100">
                                            <FontAwesomeIcon icon={faUserCheck} className="text-green-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Vérifiés</p>
                                            <p className="text-2xl font-bold text-green-600">{interviewerStats.verified}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-full bg-red-100">
                                            <FontAwesomeIcon icon={faUserTimes} className="text-red-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Non Vérifiés</p>
                                            <p className="text-2xl font-bold text-red-600">{interviewerStats.unverified}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-lg font-semibold text-gray-900">Taux de Vérification</h3>
                                    <span className="text-sm font-medium text-gray-600">{interviewerStats.verified_percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${interviewerStats.verified_percentage}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Filters and Table */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            Liste des Enquêteurs ({filteredData.length})
                                        </h2>
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                            <button
                                                onClick={() => setShowFilters(!showFilters)}
                                                className={`flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                                    showFilters 
                                                        ? 'text-white bg-red-600 hover:bg-red-700' 
                                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                                Filtres
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {showFilters && (
                                    <div className="px-6 py-4 border-t border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                                <select
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                                >
                                                    <option value="">Tous les statuts</option>
                                                    <option value="active">Actif</option>
                                                    <option value="suspended">Suspendu</option>
                                                    <option value="inactive">Inactif</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Vérification</label>
                                                <select
                                                    value={filters.verified}
                                                    onChange={(e) => handleFilterChange('verified', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="yes">Vérifiés</option>
                                                    <option value="no">Non Vérifiés</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={searchInput}
                                                    onChange={(e) => handleSearchChange(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Interviewers Table */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                {loading ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                                        <span className="ml-2 text-gray-600">Chargement...</span>
                                    </div>
                                ) : error ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="text-center">
                                            <FontAwesomeIcon icon={faTimes} className="text-red-500 text-4xl mb-4" />
                                            <p className="text-red-600 mb-4">{error}</p>
                                        </div>
                                    </div>
                                ) : filteredData.length === 0 ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="text-center">
                                            <FontAwesomeIcon icon={faUsers} className="text-gray-400 text-4xl mb-4" />
                                            <p className="text-gray-500 mb-4">Aucun {dataLabelSingular} trouvé</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vérifié</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contrat</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredData.map((person) => (
                                                    <tr 
                                                        key={person.id} 
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                        onClick={(e) => handleRowClick(person, dataType, e)}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={person.verified === 'yes' || person.verified === true}
                                                                    onChange={() => handleVerificationClick(person.id, person.verified || 'no', person.name, dataType)}
                                                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-900">
                                                                    {(person.verified === 'yes' || person.verified === true) ? 'Oui' : 'Non'}
                                                                </span>
                                                            </label>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{person.ref}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{person.name}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">{person.email}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                {getStatusIcon(person.status)}
                                                                <span className={`ml-2 text-sm ${getStatusColor(person.status)}`}>
                                                                    {getStatusText(person.status)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {(person.contrat_signed === true || person.contrat_signed === 1) ? (
                                                                    <span className="text-green-600 font-medium">Signé</span>
                                                                ) : (
                                                                    <span className="text-gray-400">Non signé</span>
                                                                )}
                                                                {person.contract_end_date && (
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        Jusqu'au {new Date(person.contract_end_date).toLocaleDateString('fr-FR')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <button
                                                                onClick={() => handleContractClick(person, dataType)}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Gérer le contrat"
                                                            >
                                                                <FontAwesomeIcon icon={faFileContract} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </li>

                    {/* Controllers Tab Content */}
                    <li className={activeTab === 1 ? 'active' : ''} style={{ display: activeTab === 1 ? 'block' : 'none' }}>
                        <div className="content__wrapper">
                            {/* Statistics */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-full bg-blue-100">
                                            <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Total Contrôleurs</p>
                                            <p className="text-2xl font-bold text-gray-900">{controllerStats.total}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-full bg-green-100">
                                            <FontAwesomeIcon icon={faUserCheck} className="text-green-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Vérifiés</p>
                                            <p className="text-2xl font-bold text-green-600">{controllerStats.verified}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="p-3 rounded-full bg-red-100">
                                            <FontAwesomeIcon icon={faUserTimes} className="text-red-600 text-xl" />
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600">Non Vérifiés</p>
                                            <p className="text-2xl font-bold text-red-600">{controllerStats.unverified}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-lg font-semibold text-gray-900">Taux de Vérification</h3>
                                    <span className="text-sm font-medium text-gray-600">{controllerStats.verified_percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${controllerStats.verified_percentage}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Filters and Table */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            Liste des Contrôleurs ({filteredData.length})
                                        </h2>
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                            <button
                                                onClick={() => setShowFilters(!showFilters)}
                                                className={`flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                                    showFilters 
                                                        ? 'text-white bg-red-600 hover:bg-red-700' 
                                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                                                Filtres
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {showFilters && (
                                    <div className="px-6 py-4 border-t border-gray-200">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                                <select
                                                    value={filters.status}
                                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                                >
                                                    <option value="">Tous les statuts</option>
                                                    <option value="active">Actif</option>
                                                    <option value="suspended">Suspendu</option>
                                                    <option value="inactive">Inactif</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Vérification</label>
                                                <select
                                                    value={filters.verified}
                                                    onChange={(e) => handleFilterChange('verified', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="yes">Vérifiés</option>
                                                    <option value="no">Non Vérifiés</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher..."
                                                    value={searchInput}
                                                    onChange={(e) => handleSearchChange(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Controllers Table */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                {loading ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                                        <span className="ml-2 text-gray-600">Chargement...</span>
                                    </div>
                                ) : error ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="text-center">
                                            <FontAwesomeIcon icon={faTimes} className="text-red-500 text-4xl mb-4" />
                                            <p className="text-red-600 mb-4">{error}</p>
                                        </div>
                                    </div>
                                ) : filteredData.length === 0 ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="text-center">
                                            <FontAwesomeIcon icon={faUsers} className="text-gray-400 text-4xl mb-4" />
                                            <p className="text-gray-500 mb-4">Aucun contrôleur trouvé</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vérifié</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contrat</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredData.map((person) => (
                                                    <tr 
                                                        key={person.id} 
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                        onClick={(e) => handleRowClick(person, 'controller', e)}
                                                    >
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={person.verified === 'yes' || person.verified === true}
                                                                    onChange={() => handleVerificationClick(person.id, person.verified || 'no', person.name, 'controller')}
                                                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                                                                />
                                                                <span className="ml-2 text-sm text-gray-900">
                                                                    {(person.verified === 'yes' || person.verified === true) ? 'Oui' : 'Non'}
                                                                </span>
                                                            </label>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{person.ref}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">{person.name}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">{person.email}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                {getStatusIcon(person.status)}
                                                                <span className={`ml-2 text-sm ${getStatusColor(person.status)}`}>
                                                                    {getStatusText(person.status)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {(person.contrat_signed === true || person.contrat_signed === 1) ? (
                                                                    <span className="text-green-600 font-medium">Signé</span>
                                                                ) : (
                                                                    <span className="text-gray-400">Non signé</span>
                                                                )}
                                                                {person.contract_end_date && (
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        Jusqu'au {new Date(person.contract_end_date).toLocaleDateString('fr-FR')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <button
                                                                onClick={() => handleContractClick(person, 'controller')}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Gérer le contrat"
                                                            >
                                                                <FontAwesomeIcon icon={faFileContract} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </li>
                </ul>
            </div>

            {/* Contract Modal */}
            {showContractModal && selectedPerson && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            Gérer le contrat - {selectedPerson.name}
                        </h2>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={contractForm.contrat_signed}
                                        onChange={(e) => setContractForm(prev => ({ ...prev, contrat_signed: e.target.checked }))}
                                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">Contrat signé</span>
                                </label>
                            </div>

                            {contractForm.contrat_signed && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date de début
                                        </label>
                                        <input
                                            type="date"
                                            value={contractForm.contract_start_date}
                                            onChange={(e) => setContractForm(prev => ({ ...prev, contract_start_date: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date de fin
                                        </label>
                                        <input
                                            type="date"
                                            value={contractForm.contract_end_date}
                                            onChange={(e) => setContractForm(prev => ({ ...prev, contract_end_date: e.target.value }))}
                                            min={contractForm.contract_start_date}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        />
                                    </div>
                                </>
                            )}

                            {selectedPerson.contract_history && selectedPerson.contract_history.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Historique des contrats
                                    </label>
                                    <div className="bg-gray-50 rounded-md p-4 max-h-40 overflow-y-auto">
                                        {selectedPerson.contract_history.map((contract, index) => (
                                            <div key={index} className="mb-2 pb-2 border-b border-gray-200 last:border-0">
                                                <div className="text-sm text-gray-700">
                                                    <span className="font-medium">Contrat #{index + 1}:</span> {' '}
                                                    {new Date(contract.start_date).toLocaleDateString('fr-FR')} - {' '}
                                                    {contract.end_date ? new Date(contract.end_date).toLocaleDateString('fr-FR') : 'En cours'}
                                                </div>
                                                {contract.signed_at && (
                                                    <div className="text-xs text-gray-500">
                                                        Signé le: {new Date(contract.signed_at).toLocaleDateString('fr-FR')}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowContractModal(false);
                                    setSelectedPerson(null);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleContractSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                            >
                                Enregistrer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && pendingVerification && (
                <ConfirmationModal
                    isOpen={showConfirmModal}
                    onClose={() => {
                        setShowConfirmModal(false);
                        setPendingVerification(null);
                    }}
                    onConfirm={handleConfirmVerification}
                    title="Confirmer le changement de statut"
                    message={
                        pendingVerification.newVerified === 'yes'
                            ? `Voulez-vous marquer le ${pendingVerification.type === 'interviewer' ? 'enquêteur' : 'contrôleur'} "${pendingVerification.name}" comme vérifié ?`
                            : `Voulez-vous marquer le ${pendingVerification.type === 'interviewer' ? 'enquêteur' : 'contrôleur'} "${pendingVerification.name}" comme non vérifié ?`
                    }
                    confirmText="Confirmer"
                    cancelText="Annuler"
                />
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <SuccessModal
                    isOpen={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                    message={successMessage}
                />
            )}

            {/* Scroll to Top Button */}
            <button 
                className={`scroll-top-button ${showScrollTop ? 'visible' : 'hidden'}`}
                onClick={scrollToTop}
                aria-label="Remonter en haut"
            >
                <svg className="svgIcon" viewBox="0 0 384 512">
                    <path
                        d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2V448c0 17.7 14.3 32 32 32s32-14.3 32-32V141.2L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z"
                    ></path>
                </svg>
            </button>

            {/* Adaptive Tabs CSS */}
            <style>{`
                .wrapper {
                    min-width: 600px;
                    max-width: 100%;
                    margin: 0 auto;
                }

                .tabs {
                    display: table;
                    table-layout: fixed;
                    width: 100%;
                    transform: translateY(5px);
                    background-color: #f3f4f6;
                    border-radius: 5px 5px 0 0;
                }

                .tabs > li {
                    transition-duration: 0.25s;
                    display: table-cell;
                    list-style: none;
                    text-align: center;
                    padding: 20px 20px 25px 20px;
                    position: relative;
                    overflow: hidden;
                    cursor: pointer;
                    color: #6b7280;
                    background-color: #f3f4f6;
                    border-bottom: 2px solid transparent;
                }

                .tabs > li:before {
                    z-index: -1;
                    position: absolute;
                    content: "";
                    width: 100%;
                    height: 120%;
                    top: 0;
                    left: 0;
                    background-color: #e5e7eb;
                    transform: translateY(100%);
                    transition-duration: 0.25s;
                    border-radius: 5px 5px 0 0;
                }

                .tabs > li:hover {
                    color: #374151;
                    background-color: #e5e7eb;
                }

                .tabs > li:hover:before {
                    transform: translateY(70%);
                }

                .tabs > li.active {
                    color: #111827;
                    background-color: white;
                    border-bottom: 2px solid #ef4444;
                    font-weight: 600;
                }

                .tabs > li.active:before {
                    transition-duration: 0.5s;
                    background-color: white;
                    transform: translateY(0);
                }

                .tab__content {
                    background-color: white;
                    position: relative;
                    width: 100%;
                    border-radius: 0 0 5px 5px;
                    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
                }

                .tab__content > li {
                    width: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    display: none;
                    list-style: none;
                }

                .tab__content > li.active {
                    display: block;
                    position: relative;
                }

                .content__wrapper {
                    text-align: left;
                    border-radius: 0 0 5px 5px;
                    width: 100%;
                    padding: 45px 40px 40px 40px;
                    background-color: white;
                }

                .scroll-top-button {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background-color: #ef4444;
                    border: none;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0px 0px 0px 4px rgba(239, 68, 68, 0.253);
                    cursor: pointer;
                    transition: opacity 0.3s ease-in-out, background-color 0.3s ease-in-out;
                    overflow: hidden;
                    position: fixed;
                    bottom: 30px;
                    right: 30px;
                    z-index: 1000;
                    opacity: 0;
                    pointer-events: none;
                }

                .scroll-top-button.visible {
                    opacity: 1;
                    pointer-events: auto;
                }

                .scroll-top-button.hidden {
                    opacity: 0;
                    pointer-events: none;
                }

                .scroll-top-button .svgIcon {
                    width: 12px;
                    transition-duration: 0.3s;
                }

                .scroll-top-button .svgIcon path {
                    fill: white;
                }

                .scroll-top-button:hover {
                    width: 140px;
                    border-radius: 50px;
                    transition-duration: 0.3s;
                    background-color: #ef4444;
                    align-items: center;
                }

                .scroll-top-button:hover .svgIcon {
                    transition-duration: 0.3s;
                    transform: translateY(-200%);
                }

                .scroll-top-button::before {
                    position: absolute;
                    bottom: -20px;
                    content: "Remonter";
                    color: white;
                    font-size: 0px;
                    transition-duration: 0.3s;
                }

                .scroll-top-button:hover::before {
                    font-size: 13px;
                    opacity: 1;
                    bottom: unset;
                    transition-duration: 0.3s;
                }
            `}</style>
        </div>
    );
};

export default InterviewerVerification;
